import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { clsx } from 'clsx'
import { getLeaderboard, getWinner, getStanding } from '../api/gg'
import { useAuth } from '../store/auth'
import type { MonthlyWinner, ScoreEntry } from '../types'

type Range = 'all' | 'month'

interface Standing {
  score: number
  plays: number
  rank: number
}

/** Best display label for an entry: the chosen goon name, or a fallback. */
function nameOf(e: Pick<ScoreEntry, 'name'>): string {
  return e.name ?? 'anon goon'
}

const MEDALS = ['🥇', '🥈', '🥉']

/** Per-rank emphasis for the top three. */
function rankAccent(rank: number): {
  row: string
  badge: string
  score: string
  medal: string | null
} {
  switch (rank) {
    case 1:
      return {
        row: 'border-zap/60 bg-zap/10 shadow-[0_0_18px_rgba(255,210,62,0.25)]',
        badge: 'border-zap bg-zap/20 text-zap shadow-[0_0_12px_rgba(255,210,62,0.5)]',
        score: 'text-zap [text-shadow:0_0_12px_rgba(255,210,62,0.6)]',
        medal: MEDALS[0],
      }
    case 2:
      return {
        row: 'border-kapow/50 bg-kapow/10 shadow-[0_0_16px_rgba(32,227,255,0.2)]',
        badge: 'border-kapow bg-kapow/20 text-kapow shadow-[0_0_12px_rgba(32,227,255,0.45)]',
        score: 'neon-cyan',
        medal: MEDALS[1],
      }
    case 3:
      return {
        row: 'border-boom/50 bg-boom/10 shadow-[0_0_16px_rgba(168,85,247,0.2)]',
        badge: 'border-boom bg-boom/20 text-boom shadow-[0_0_12px_rgba(168,85,247,0.45)]',
        score: 'text-boom [text-shadow:0_0_12px_rgba(168,85,247,0.55)]',
        medal: MEDALS[2],
      }
    default:
      return {
        row: 'border-edge bg-surface2/60',
        badge: 'border-edge bg-[#0e0e18] text-muted',
        score: 'neon-pink',
        medal: null,
      }
  }
}

/** Render an `at://did/app.bsky.feed.post/rkey` as a clickable bsky.app URL. */
function bskyUrlFromAtUri(uri: string): string | null {
  const m = uri.match(/^at:\/\/([^/]+)\/app\.bsky\.feed\.post\/([^/]+)$/)
  if (!m) return null
  return `https://bsky.app/profile/${m[1]}/post/${m[2]}`
}

/** Pretty `YYYY-MM` (or any Date-parsable) month label. */
function monthLabel(month: string): string {
  const d = new Date(/^\d{4}-\d{2}$/.test(month) ? `${month}-01T00:00:00Z` : month)
  if (Number.isNaN(d.getTime())) return month
  return d.toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' })
}

export function ScoreBoard() {
  const user = useAuth((s) => s.user)
  const token = useAuth((s) => s.token)

  const [range, setRange] = useState<Range>('all')
  const [entries, setEntries] = useState<ScoreEntry[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [winner, setWinner] = useState<MonthlyWinner | null>(null)
  const [standing, setStanding] = useState<Standing | null>(null)

  // Leaderboard: load on mount and whenever the range toggles.
  const load = useCallback(async (r: Range) => {
    setLoading(true)
    setError(null)
    try {
      const res = await getLeaderboard(r)
      setEntries(res.entries)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load the Score Board.')
      setEntries([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load(range)
  }, [range, load])

  // Last monthly winner — independent, degrades to null on failure.
  useEffect(() => {
    let alive = true
    void getWinner()
      .then((w) => {
        if (alive) setWinner(w)
      })
      .catch(() => {
        if (alive) setWinner(null)
      })
    return () => {
      alive = false
    }
  }, [])

  // Your personal standing — only when signed in.
  useEffect(() => {
    if (!user || !token) {
      setStanding(null)
      return
    }
    let alive = true
    void getStanding(token)
      .then((s) => {
        if (alive) setStanding(s)
      })
      .catch(() => {
        if (alive) setStanding(null)
      })
    return () => {
      alive = false
    }
  }, [user, token])

  const top = entries[0] ?? null
  const winnerUrl = winner?.postUri ? bskyUrlFromAtUri(winner.postUri) : null

  return (
    <main className="mx-auto max-w-3xl px-4 py-8 pb-28">
      <h1 className="goon-title comic-title-cyan mb-6 text-center text-5xl text-white">
        LEADERBOARD
      </h1>

      {/* Range toggle — neon segmented control */}
      <div className="mb-6 flex justify-center">
        <div className="inline-flex gap-1 rounded-full border border-edge bg-surface/80 p-1 shadow-panel backdrop-blur">
          <RangeButton active={range === 'all'} onClick={() => setRange('all')}>
            All-time
          </RangeButton>
          <RangeButton active={range === 'month'} onClick={() => setRange('month')}>
            This month
          </RangeButton>
        </div>
      </div>

      {/* Hero: biggest goon */}
      <section className="mb-8">
        {loading && !entries.length ? (
          <div className="panel grid place-items-center p-8">
            <span className="comic-title comic-title-cyan animate-pulse text-3xl text-white">
              Counting goons…
            </span>
          </div>
        ) : top ? (
          <div className="panel panel-pink relative overflow-hidden p-6 text-center shadow-panel-lg">
            <div className="pointer-events-none absolute inset-0 opacity-10 bg-[repeating-linear-gradient(transparent,transparent_2px,rgba(255,255,255,0.03)_3px)]" />
            <div className="relative">
              <p className="goon-title text-2xl text-white">🏆 BIGGEST GOON</p>
              <p className="mt-2 truncate text-2xl font-bold text-white">{nameOf(top)}</p>
              <p className="comic-title my-1 text-6xl neon-pink">{top.score.toLocaleString()}</p>
              <p className="font-comic text-xl uppercase tracking-wide text-[#cfcfe6]">
                {top.plays.toLocaleString()} {top.plays === 1 ? 'play' : 'plays'}
                {top.bestCombo > 1 ? ` · best combo ×${top.bestCombo}` : ''}
              </p>
            </div>
          </div>
        ) : (
          <div className="panel grid place-items-center p-8 text-center">
            <p className="comic-title text-3xl text-white">No scores yet</p>
            <p className="mt-2 font-comic text-xl text-muted">— be the first!</p>
            <Link to="/" className="btn-goon mt-4">
              Play now
            </Link>
          </div>
        )}
      </section>

      {/* Error (leaderboard) */}
      {error ? (
        <div className="panel panel-pink mb-6 px-4 py-3 text-center font-bold text-white">
          {error}{' '}
          <button onClick={() => void load(range)} className="text-pow underline hover:brightness-125">
            Retry
          </button>
        </div>
      ) : null}

      {/* Ranked list */}
      {entries.length > 0 ? (
        <section className="panel space-y-2 p-3">
          {entries.map((e) => {
            const { row, badge, score, medal } = rankAccent(e.rank)
            return (
              <div
                key={e.userId}
                className={clsx(
                  'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition hover:brightness-110',
                  row,
                )}
              >
                <span
                  className={clsx(
                    'grid h-10 w-10 shrink-0 place-items-center rounded-lg border font-comic text-xl',
                    badge,
                  )}
                >
                  {medal ?? <span>#{e.rank}</span>}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-white">{nameOf(e)}</p>
                  <p className="text-xs uppercase tracking-wide text-muted">
                    {e.plays.toLocaleString()} {e.plays === 1 ? 'play' : 'plays'}
                    {e.bestCombo > 1 ? ` · combo ×${e.bestCombo}` : ''}
                  </p>
                </div>
                <span className={clsx('shrink-0 font-comic text-2xl', score)}>
                  {e.score.toLocaleString()}
                </span>
              </div>
            )
          })}
        </section>
      ) : null}

      {/* Last monthly winner */}
      {winner ? (
        <section className="mt-10">
          <h2 className="comic-title comic-title-cyan mb-3 text-3xl text-white">LAST MONTH'S CHAMP</h2>
          <div className="panel panel-cyan relative overflow-hidden p-5">
            <div className="pointer-events-none absolute inset-0 opacity-8 bg-[repeating-linear-gradient(transparent,transparent_2px,rgba(255,255,255,0.03)_3px)]" />
            <div className="relative">
              <p className="font-comic text-xl uppercase tracking-wide neon-cyan">
                {monthLabel(winner.month)}
              </p>
              <p className="mt-1 text-lg text-[#cfcfe6]">
                <span className="font-bold text-white">
                  {winner.name ?? 'a mystery goon'}
                </span>{' '}
                won with{' '}
                <span className="comic-title text-2xl text-zap [text-shadow:0_0_12px_rgba(255,210,62,0.6)]">
                  {winner.score.toLocaleString()}
                </span>
              </p>
              {winner.trendingTags.length > 0 ? (
                <div className="mt-3 flex flex-wrap gap-2">
                  {winner.trendingTags.map((t) => (
                    <span
                      key={t}
                      className="rounded-full border border-boom/60 bg-boom/15 px-3 py-0.5 text-sm font-bold text-boom shadow-[0_0_10px_rgba(168,85,247,0.3)]"
                    >
                      #{t}
                    </span>
                  ))}
                </div>
              ) : null}
              {winner.postUri ? (
                winnerUrl ? (
                  <a
                    href={winnerUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mt-4 inline-block font-bold text-zap underline hover:brightness-125"
                  >
                    See the announcement →
                  </a>
                ) : (
                  <p className="mt-4 text-sm text-muted">Announcement posted to Bluesky.</p>
                )
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      {/* Sticky standing / sign-in hint */}
      <div className="fixed inset-x-0 bottom-0 z-10 border-t border-edge bg-ink/90 px-4 py-3 shadow-[0_-8px_30px_rgba(0,0,0,0.5)] backdrop-blur">
        <div className="mx-auto max-w-3xl text-center">
          {user && token ? (
            standing ? (
              <p className="font-comic text-lg uppercase tracking-wide text-[#cfcfe6]">
                Your best:{' '}
                <span className="neon-pink">{standing.score.toLocaleString()}</span> · rank{' '}
                <span className="text-zap [text-shadow:0_0_10px_rgba(255,210,62,0.5)]">
                  #{standing.rank}
                </span>{' '}
                ·{' '}
                <span className="neon-cyan">
                  {standing.plays.toLocaleString()} {standing.plays === 1 ? 'play' : 'plays'}
                </span>
              </p>
            ) : (
              <p className="font-comic text-lg uppercase tracking-wide text-muted">
                No banked score yet —{' '}
                <Link to="/" className="text-zap underline hover:brightness-125">
                  go goon
                </Link>
              </p>
            )
          ) : (
            <p className="text-sm text-muted">
              <Link to="/login" className="font-bold text-zap underline hover:brightness-125">
                Sign in
              </Link>{' '}
              to rank on the Score Board.
            </p>
          )}
        </div>
      </div>
    </main>
  )
}

function RangeButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      onClick={onClick}
      className={clsx(
        'rounded-full px-5 py-1.5 font-comic text-lg uppercase tracking-wide transition',
        active
          ? 'bg-pow text-white shadow-neon-pink'
          : 'bg-transparent text-muted hover:text-white',
      )}
    >
      {children}
    </button>
  )
}
